"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { collectionGroup, doc, documentId, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { auth, db } from "@/lib/firebase/client";
import { resolveTenantIdFromBrowser } from "@/lib/tenant/tenantId";

// 立場（ロール）の定義：社員、マネージャー、人事、社労士、BPS（外部コーチ）
export type UserRole = "employee" | "manager" | "hr" | "sharoushi" | "bps";

type AuthState = {
  user: User | null;
  role: UserRole | null;
  companyId: string | null;
  companyIds: string[];
  tenantId: string | null;
  loading: boolean;
};

const AuthContext = createContext<AuthState>({
  user: null,
  role: null,
  companyId: null,
  companyIds: [],
  tenantId: null,
  loading: true,
});

/**
 * データベース（Firestore）からユーザーの役割を取得する関数
 */
async function fetchRoleFromArtifacts(uid: string): Promise<UserRole | null> {
  try {
    // artifacts/manenobi-ai/users/{uid}/profile/data
    // collection: artifacts -> doc: manenobi-ai -> collection: users -> doc: {uid} -> collection: profile -> doc: data
    const snap = await getDoc(doc(db, "artifacts", "manenobi-ai", "users", uid, "profile", "data"));
    if (!snap.exists()) return null;
    
    const data = snap.data() as { role?: unknown };
    const role = typeof data.role === "string" ? data.role : null;
    if (role === "employee" || role === "manager" || role === "hr" || role === "sharoushi" || role === "bps") return role;
    return null;
  } catch (e) {
    console.error("Error fetching role:", e);
    return null;
  }
}

type UserProfileDoc = {
  role?: unknown;
  companyId?: unknown;
  companyIds?: unknown;
};

type UserRootDoc = {
  role?: unknown;
  companyIds?: unknown;
  primaryTenantId?: unknown;
};

async function fetchProfileFromUsers(uid: string): Promise<UserProfileDoc | null> {
  try {
    const snap = await getDoc(doc(db, "users", uid, "profile", "data"));
    if (!snap.exists()) return null;
    return (snap.data() as UserProfileDoc) ?? null;
  } catch (e) {
    console.error("Error fetching profile (users/{uid}/profile/data):", e);
    return null;
  }
}

function normalizeRole(raw: unknown): UserRole | null {
  return raw === "employee" || raw === "manager" || raw === "hr" || raw === "sharoushi" || raw === "bps" ? (raw as UserRole) : null;
}

function normalizeCompanyIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v) => typeof v === "string" && v.trim().length > 0) as string[];
}

async function fetchUserRoot(uid: string): Promise<UserRootDoc | null> {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return null;
    return (snap.data() as UserRootDoc) ?? null;
  } catch (e) {
    console.error("Error fetching user root (users/{uid}):", e);
    return null;
  }
}

async function fetchPrimaryTenantId(uid: string): Promise<string | null> {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return null;
    const data = snap.data() as { primaryTenantId?: unknown };
    return typeof data.primaryTenantId === "string" ? data.primaryTenantId : null;
  } catch (e) {
    console.error("Error fetching primaryTenantId:", e);
    return null;
  }
}

async function findTenantIdFromMembership(uid: string): Promise<string | null> {
  try {
    const q = query(collectionGroup(db, "memberships"), where(documentId(), "==", uid), limit(1));
    const snaps = await getDocs(q);
    const first = snaps.docs[0];
    if (!first) return null;
    const tenantDoc = first.ref.parent.parent;
    return tenantDoc ? tenantDoc.id : null;
  } catch (e) {
    console.error("Error resolving tenantId from memberships:", e);
    return null;
  }
}

async function fetchRoleFromMembership(tenantId: string, uid: string): Promise<UserRole | null> {
  try {
    const snap = await getDoc(doc(db, "tenants", tenantId, "memberships", uid));
    if (!snap.exists()) return null;
    const data = snap.data() as { role?: unknown };
    const role = typeof data.role === "string" ? data.role : null;
    if (role === "employee" || role === "manager" || role === "hr" || role === "sharoushi" || role === "bps") return role;
    return null;
  } catch (e) {
    console.error("Error fetching role (membership):", e);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let finished = false;
    const watchdog = setTimeout(() => {
      if (finished) return;
      setLoading(false);
    }, 10_000);

    const withTimeout = async <T,>(p: Promise<T>, ms: number): Promise<T> => {
      return await Promise.race([
        p,
        new Promise<T>((_, reject) =>
          setTimeout(() => {
            reject(new Error("auth_init_timeout"));
          }, ms),
        ),
      ]);
    };

    // Firebaseのログイン状態を監視
    const unsub = onAuthStateChanged(auth, async (u: User | null) => {
      finished = true;
      clearTimeout(watchdog);

      setUser(u);
      setRole(null);
      setCompanyId(null);
      setCompanyIds([]);
      setTenantId(null);

      if (!u) {
        setLoading(false);
        return;
      }

      try {
        const hostTenant = resolveTenantIdFromBrowser({
          defaultTenantId: process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || null,
        }).tenantId;

        const [profile, userRoot] = await withTimeout(
          Promise.all([fetchProfileFromUsers(u.uid), fetchUserRoot(u.uid)]),
          8_000,
        );

        const profileRole = normalizeRole(profile?.role);
        const rootRole = normalizeRole(userRoot?.role);
        const resolvedSharoushiRole = profileRole === "sharoushi" || rootRole === "sharoushi" ? "sharoushi" : null;

        const profileCompanyId = typeof profile?.companyId === "string" ? profile.companyId : null;
        const profileCompanyIds = normalizeCompanyIds(profile?.companyIds);
        const rootCompanyIds = normalizeCompanyIds(userRoot?.companyIds);
        const resolvedCompanyIds = profileCompanyIds.length > 0 ? profileCompanyIds : rootCompanyIds;

        const fallbackCompanyId =
          (typeof userRoot?.primaryTenantId === "string" ? userRoot.primaryTenantId : null) ||
          (await withTimeout(fetchPrimaryTenantId(u.uid), 6_000).catch(() => null)) ||
          (await withTimeout(findTenantIdFromMembership(u.uid), 6_000).catch(() => null));

        const resolvedCompanyId = hostTenant || profileCompanyId || fallbackCompanyId;

        setCompanyId(resolvedCompanyId);
        setTenantId(resolvedCompanyId);
        setCompanyIds(resolvedCompanyIds);

        if (resolvedCompanyId) {
          try {
            const token = await u.getIdToken();
            await fetch("/api/tenant/bootstrap", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({}),
            });
          } catch (e) {
            console.error("Failed to bootstrap tenant:", e);
          }
        }

        // Role resolution policy:
        // - sharoushi is a global role (can access multiple tenants), so prefer users/{uid}/profile/data.role.
        // - employee/manager/hr are tenant-scoped, so prefer tenants/{tenantId}/memberships/{uid}.role.
        if (resolvedSharoushiRole === "sharoushi") {
          setRole("sharoushi");
        } else if (resolvedCompanyId) {
          const membershipRole = await withTimeout(fetchRoleFromMembership(resolvedCompanyId, u.uid), 6_000).catch(() => null);
          const resolvedRole = membershipRole || profileRole || rootRole || ((await fetchRoleFromArtifacts(u.uid)) || null);
          setRole(resolvedRole || "employee");
        } else {
          const resolvedRole = profileRole || rootRole || ((await fetchRoleFromArtifacts(u.uid)) || null);
          setRole(resolvedRole || "employee");
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      finished = true;
      clearTimeout(watchdog);
      unsub();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, role, companyId, companyIds, tenantId, loading }),
    [user, role, companyId, companyIds, tenantId, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 他の画面からログイン情報や役割を簡単に使うためのフック
 */
export function useAuth() {
  return useContext(AuthContext);
}